import { PaginationEnum } from '@components/custom/gallery/paginationEnum';
import { useSession } from '@context/sessionProvider';
import useSWR from 'swr';
import type { ConfigData } from '@/types';
import { Category, ErrorProps, Product } from '@/types';

export type GridConfig = ConfigData & {
	id: number;
};

export type GridResponse = {
	data?: GridConfig[];
	message?: string;
};

export type DefaultBackground = {
	id: number;
	name: string;
	image_path: string;
};

export type DefaultBackgroundRes = {
	data: DefaultBackground[];
	total: number;
};

export type ProductsData = {
	total: number;
	page: number;
	limit: number;
	products: Product[];
};

export type ProductsResponse = {
	data?: ProductsData;
	message?: string;
};

export type ProductsByExternalIdsData = {
	products: Product[];
	missingExternalIds: number[];
};

export type ProductsByExternalIdsResponse = {
	data?: ProductsByExternalIdsData;
	message?: string;
};

export type CategoryListData = {
	total: number;
	page: number;
	limit: number;
	categories: Category[];
};

export type CategoryListResponse = {
	data?: CategoryListData;
	message?: string;
};

export type CategoriesByExternalIdsData = {
	categories: Category[];
	missingExternalIds: number[];
};

export type CategoriesByExternalIdsResponse = {
	data?: CategoriesByExternalIdsData;
	message?: string;
};

type CatalogQueryParams = {
	search?: string;
	page?: number;
	limit?: number;
	excludeExternalIds?: number[];
	enabled?: boolean;
};

type FetcherKey = [string] | [string, Record<string, string | number | undefined>];

export const createFetcher = (token: string) => async (queryParams: FetcherKey) => {
	const headers = new Headers({
		'Content-Type': 'application/json',
	});

	if (token) {
		headers.append('Authorization', `Bearer ${token}`);
	}

	const rawParams = queryParams[1] ?? {};
	const params: Record<string, string> = {};

	for (const [key, value] of Object.entries(rawParams)) {
		if (value !== undefined) {
			params[key] = String(value);
		}
	}

	const query = new URLSearchParams(params).toString();
	const fullUrl = query ? `${queryParams[0]}?${query}` : queryParams[0];

	const options: RequestInit = {
		method: 'GET',
		headers: headers,
	};

	const res = await fetch(fullUrl, options);

	if (!res.ok) {
		throw await createFetchError(res, 'An error occurred while fetching the data.');
	}

	return res.json();
};

export function useConfigs() {
	const { context } = useSession();
	const fetcherWithToken = createFetcher(context);

	const getKey = () => ['/api/config/get_all'];
	const { data, error, isLoading, mutate } = useSWR<GridResponse>(getKey, fetcherWithToken, {
		refreshInterval: 0,
	});

	return {
		configs: data?.data,
		error,
		isLoading,
		mutate,
	};
}

export function useGallery(page = PaginationEnum.page, limit = PaginationEnum.limit) {
	const { context } = useSession();
	const fetcherWithToken = createFetcher(context);

	const getKey = () => ['/api/backgrounds/default', { page, limit }];

	const { data, error, isLoading, mutate } = useSWR<DefaultBackgroundRes>(getKey, fetcherWithToken, {
		refreshInterval: 0,
		keepPreviousData: true,
		dedupingInterval: 5000,
	});

	return {
		data: data?.data || [],
		total: data?.total || 0,
		error,
		isLoading,
		mutate,
	};
}

export function useProducts({
	search = '',
	page = 1,
	limit = 25,
	excludeExternalIds = [],
	enabled = true,
}: CatalogQueryParams) {
	const { context } = useSession();
	const fetcherWithToken = createFetcher(context);

	const getKey = () =>
		enabled ? ['/api/products', { search, page, limit, excludeExternalIds: toIdListParam(excludeExternalIds) }] : null;

	const { data, error, isLoading, mutate } = useSWR<ProductsResponse>(getKey, fetcherWithToken, {
		refreshInterval: 0,
		keepPreviousData: true,
		dedupingInterval: 5000,
	});
	const productsData = data?.data;
	const typedError = error as ErrorProps | undefined;

	return {
		products: productsData?.products ?? [],
		total: productsData?.total ?? 0,
		page: productsData?.page ?? page,
		limit: productsData?.limit ?? limit,
		error: typedError,
		isLoading: enabled ? isLoading : false,
		isMissingScope: isProductScopeError(typedError),
		isCatalogExhausted: enabled && !search.trim() && !isLoading && !typedError && (productsData?.total ?? 0) === 0,
		mutate,
	};
}

export function useCategoryList({
	search = '',
	page = 1,
	limit = 25,
	excludeExternalIds = [],
	enabled = true,
}: CatalogQueryParams) {
	const { context } = useSession();
	const fetcherWithToken = createFetcher(context);

	const getKey = () =>
		enabled
			? ['/api/categories/list', { search, page, limit, excludeExternalIds: toIdListParam(excludeExternalIds) }]
			: null;

	const { data, error, isLoading, mutate } = useSWR<CategoryListResponse>(getKey, fetcherWithToken, {
		refreshInterval: 0,
		keepPreviousData: true,
		dedupingInterval: 5000,
	});
	const categoryListData = data?.data;
	const typedError = error as ErrorProps | undefined;

	return {
		categories: categoryListData?.categories ?? [],
		total: categoryListData?.total ?? 0,
		page: categoryListData?.page ?? page,
		limit: categoryListData?.limit ?? limit,
		error: typedError,
		isLoading: enabled ? isLoading : false,
		isMissingScope: isProductScopeError(typedError),
		isCatalogExhausted: enabled && !search.trim() && !isLoading && !typedError && (categoryListData?.total ?? 0) === 0,
		mutate,
	};
}

export function useCategoriesByExternalIds(externalIds: number[], enabled = true) {
	const { context } = useSession();
	const selectedExternalIds = normalizeExternalIds(externalIds);
	const selectedExternalIdsKey = selectedExternalIds.join(',');

	const { data, error, isLoading, mutate } = useSWR<CategoriesByExternalIdsData>(
		enabled && selectedExternalIds.length ? ['categories-by-external-ids', selectedExternalIdsKey] : null,
		() => fetchCategoriesByExternalIds(context, selectedExternalIds),
		{
			refreshInterval: 0,
			keepPreviousData: true,
			dedupingInterval: 5000,
		},
	);
	const typedError = error as ErrorProps | undefined;

	return {
		categories: data?.categories ?? [],
		missingExternalIds: data?.missingExternalIds ?? [],
		error: typedError,
		isLoading: enabled && selectedExternalIds.length > 0 ? isLoading : false,
		isMissingScope: isProductScopeError(typedError),
		mutate,
	};
}

export async function fetchCategoriesByExternalIds(
	token: string,
	externalIds: number[],
): Promise<CategoriesByExternalIdsData> {
	const normalizedExternalIds = normalizeExternalIds(externalIds);

	if (normalizedExternalIds.length === 0) {
		return { categories: [], missingExternalIds: [] };
	}

	const headers = new Headers({
		'Content-Type': 'application/json',
	});

	if (token) {
		headers.append('Authorization', `Bearer ${token}`);
	}

	const res = await fetch('/api/categories/by-external-ids', {
		method: 'POST',
		headers,
		body: JSON.stringify({ externalIds: normalizedExternalIds }),
	});

	if (!res.ok) {
		throw await createFetchError(res, 'An error occurred while fetching categories.');
	}

	const response = (await res.json()) as CategoriesByExternalIdsResponse;

	return response.data ?? { categories: [], missingExternalIds: normalizedExternalIds };
}

export function useProductsByExternalIds(externalIds: number[], enabled = true) {
	const { context } = useSession();
	const selectedExternalIds = normalizeExternalIds(externalIds);
	const selectedExternalIdsKey = selectedExternalIds.join(',');

	const { data, error, isLoading, mutate } = useSWR<ProductsByExternalIdsData>(
		enabled && selectedExternalIds.length ? ['products-by-external-ids', selectedExternalIdsKey] : null,
		() => fetchProductsByExternalIds(context, selectedExternalIds),
		{
			refreshInterval: 0,
			keepPreviousData: true,
			dedupingInterval: 5000,
		},
	);
	const typedError = error as ErrorProps | undefined;

	return {
		products: data?.products ?? [],
		missingExternalIds: data?.missingExternalIds ?? [],
		error: typedError,
		isLoading: enabled && selectedExternalIds.length > 0 ? isLoading : false,
		isMissingScope: isProductScopeError(typedError),
		mutate,
	};
}

export async function fetchProductsByExternalIds(
	token: string,
	externalIds: number[],
): Promise<ProductsByExternalIdsData> {
	const normalizedExternalIds = normalizeExternalIds(externalIds);

	if (normalizedExternalIds.length === 0) {
		return { products: [], missingExternalIds: [] };
	}

	const headers = new Headers({
		'Content-Type': 'application/json',
	});

	if (token) {
		headers.append('Authorization', `Bearer ${token}`);
	}

	const res = await fetch('/api/products/by-external-ids', {
		method: 'POST',
		headers,
		body: JSON.stringify({ externalIds: normalizedExternalIds }),
	});

	if (!res.ok) {
		throw await createFetchError(res, 'An error occurred while fetching products.');
	}

	const response = (await res.json()) as ProductsByExternalIdsResponse;

	return response.data ?? { products: [], missingExternalIds: normalizedExternalIds };
}

function normalizeExternalIds(externalIds: number[]): number[] {
	return [...new Set(externalIds.filter((externalId) => Number.isInteger(externalId) && externalId > 0))];
}

/** `undefined` keeps the parameter out of both the SWR key and the query string when there is nothing to send. */
function toIdListParam(externalIds: number[]): string | undefined {
	const normalizedExternalIds = normalizeExternalIds(externalIds);

	return normalizedExternalIds.length ? normalizedExternalIds.join(',') : undefined;
}

async function createFetchError(res: Response, fallbackMessage: string): Promise<ErrorProps> {
	const body = await parseErrorBody(res);
	const error: ErrorProps = new Error(typeof body.message === 'string' ? body.message : fallbackMessage);
	error.status = res.status;
	error.code = typeof body.code === 'string' ? body.code : undefined;
	error.requiredScopes = Array.isArray(body.requiredScopes)
		? body.requiredScopes.filter((scope): scope is string => typeof scope === 'string')
		: undefined;

	return error;
}

async function parseErrorBody(res: Response): Promise<Record<string, unknown>> {
	try {
		const body = await res.json();

		return typeof body === 'object' && body !== null && !Array.isArray(body) ? body : {};
	} catch {
		return {};
	}
}

function isProductScopeError(error: ErrorProps | undefined): boolean {
	return error?.status === 403 && error.code === 'PRODUCT_SCOPE_REQUIRED';
}
